import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Tooltip as LeafletTooltip,
  LayersControl,
  Marker,
  Popup,
  CircleMarker,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import {
  Download,
  FileJson,
  Layers,
  X,
  Warehouse,
  Plus,
  Trash2,
  MapPin,
  ChevronRight,
  Circle as CircleIcon,
  Route as RouteIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useData } from "../lib/data-context";
import { api, API, viabilidadColor, fmtDec, fmtMWh, fmtInt } from "../lib/api";

const TAMANOS_OPTS = ["8 m", "10 m", "12 m", "15 m", "18 m"];
// CARTO Basemaps API key (client-side, tokens `cb1_` son públicos por diseño)
const CARTO_KEY = process.env.REACT_APP_CARTO_API_KEY || "";
// Paleta extensible: las primeras 4 concesiones mantienen los colores oficiales,
// el resto recibe un color del pool para diferenciar visualmente en el mapa.
const LOTE_COLOR_FIXED = { "CM-11": "#14532D", "CM-31": "#365314", "CM-32": "#7C2D12", "CM-41": "#15803D" };
const LOTE_COLOR_POOL = ["#0F766E", "#1D4ED8", "#9D174D", "#A16207", "#5B21B6", "#B45309", "#0E7490", "#4D7C0F"];
const loteColor = (lote, allLotes = []) => {
  if (LOTE_COLOR_FIXED[lote]) return LOTE_COLOR_FIXED[lote];
  const extras = allLotes.filter((l) => !LOTE_COLOR_FIXED[l]);
  const idx = extras.indexOf(lote);
  if (idx >= 0) return LOTE_COLOR_POOL[idx % LOTE_COLOR_POOL.length];
  return "#374151";
};

const cocheraIcon = (color = "#0F1A14") =>
  L.divIcon({
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
    html: `
      <div style="position:relative;">
        <div style="width:30px;height:30px;background:${color};border:2px solid white;
                    border-radius:50% 50% 50% 0;transform:rotate(-45deg);
                    box-shadow:0 2px 6px rgba(0,0,0,0.3);
                    display:flex;align-items:center;justify-content:center;">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
               viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"
               style="transform:rotate(45deg);">
            <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35"/>
            <path d="M22 6L12 2 2 6"/>
            <path d="M6 18h12"/>
            <path d="M6 14h12"/>
          </svg>
        </div>
      </div>
    `,
  });

function MapClickHandler({ enabled, onPick }) {
  useMapEvents({
    click(e) {
      if (enabled) onPick(e.latlng);
    },
  });
  return null;
}

export default function Mapa() {
  const { lineas, lotes, loading } = useData();

  // Filters
  const [filterLote, setFilterLote] = useState("");
  const [filterLinea, setFilterLinea] = useState("");
  const [filterSublinea, setFilterSublinea] = useState("");
  const [filterTamano, setFilterTamano] = useState("");
  const [filterViab, setFilterViab] = useState("");

  // Data
  const [geojsonMeta, setGeojsonMeta] = useState(null);
  const [sublineasGeojson, setSublineasGeojson] = useState(null);
  const [paradasGeojson, setParadasGeojson] = useState(null);
  const [paradasLinea, setParadasLinea] = useState(null); // detalle de la línea seleccionada

  // UI
  const [showSublineas, setShowSublineas] = useState(false);
  const [showParadas, setShowParadas] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);

  // Cocheras
  const [cocheras, setCocheras] = useState([]);
  const [showCocheraForm, setShowCocheraForm] = useState(false);
  const [form, setForm] = useState({ nombre: "", lote: "", capacidad_vehiculos: 50, lat: "", lon: "", observaciones: "" });
  const [pickFromMap, setPickFromMap] = useState(false);

  useEffect(() => {
    api.get("/geojson/metadata").then((r) => setGeojsonMeta(r.data)).catch(() => {});
    refreshCocheras();
  }, []);

  const refreshCocheras = async () => {
    try {
      const r = await api.get("/cocheras");
      setCocheras(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadSublineas = useCallback(async () => {
    if (sublineasGeojson) return sublineasGeojson;
    const r = await api.get("/sublineas");
    setSublineasGeojson(r.data);
    return r.data;
  }, [sublineasGeojson]);

  // Carga sublíneas cuando se activa toggle, se filtra por línea o se selecciona línea
  useEffect(() => {
    if ((showSublineas || filterLinea || selectedLine) && !sublineasGeojson) {
      loadSublineas();
    }
  }, [showSublineas, filterLinea, selectedLine, sublineasGeojson, loadSublineas]);

  // Carga paradas si toggle ON o hay línea filtrada/seleccionada
  useEffect(() => {
    if ((showParadas || filterLinea) && !paradasGeojson) {
      api.get("/paradas").then((r) => setParadasGeojson(r.data)).catch(() => {});
    }
  }, [showParadas, filterLinea, paradasGeojson]);

  // Carga detalle de paradas por sublínea cuando se selecciona/filtra una línea
  useEffect(() => {
    const cod = filterLinea || selectedLine?.codigo_linea;
    if (cod) {
      api.get(`/paradas/${encodeURIComponent(cod)}`).then((r) => setParadasLinea(r.data)).catch(() => setParadasLinea(null));
    } else {
      setParadasLinea(null);
    }
  }, [filterLinea, selectedLine]);

  const lineByCode = useMemo(() => {
    const m = {};
    (lineas || []).forEach((l) => { m[l.codigo_linea] = l; });
    return m;
  }, [lineas]);

  // Líneas disponibles según lote
  const lineasFiltradas = useMemo(() => {
    return (lineas || [])
      .filter((l) => !filterLote || l.lote === filterLote)
      .sort((a, b) => a.numero_linea.localeCompare(b.numero_linea, "es", { numeric: true }));
  }, [lineas, filterLote]);

  // Cuando cambia el lote, limpiar selector de línea si no aplica
  useEffect(() => {
    if (filterLinea && !lineasFiltradas.find((l) => l.codigo_linea === filterLinea)) {
      setFilterLinea("");
      setFilterSublinea("");
    }
  }, [filterLote, filterLinea, lineasFiltradas]);

  // Sublíneas disponibles según línea filtrada
  const sublineasDisponibles = useMemo(() => {
    if (!filterLinea || !paradasLinea) return [];
    const out = [];
    Object.entries(paradasLinea).forEach(([sentido, its]) => {
      Object.entries(its).forEach(([itinerario, paradas]) => {
        out.push({
          itinerario,
          sentido,
          n_paradas: paradas.length,
          paradas,
        });
      });
    });
    return out;
  }, [filterLinea, paradasLinea]);

  useEffect(() => {
    // Reset sublínea si ya no existe
    if (filterSublinea && !sublineasDisponibles.find((s) => s.itinerario === filterSublinea)) {
      setFilterSublinea("");
    }
  }, [sublineasDisponibles, filterSublinea]);

  const passFilter = useCallback(
    (l) => {
      if (filterLote && l.lote !== filterLote) return false;
      if (filterLinea && l.codigo_linea !== filterLinea) return false;
      if (filterTamano && l.tamano_vehiculo !== filterTamano) return false;
      if (filterViab && l.viabilidad !== filterViab) return false;
      return true;
    },
    [filterLote, filterLinea, filterTamano, filterViab]
  );

  // Vista de líneas principales
  const mainLines = useMemo(() => {
    return (lineas || []).filter((l) => l.geometria && l.geometria.length >= 2 && passFilter(l));
  }, [lineas, passFilter]);

  // Vista de sublíneas (cuando toggle ON o línea filtrada)
  const subFeatures = useMemo(() => {
    if (!sublineasGeojson) return [];
    return sublineasGeojson.features.filter((f) => {
      const p = f.properties;
      const linea = lineByCode[p.codigo_linea];
      if (!linea) return false;
      if (!passFilter(linea)) return false;
      if (filterSublinea && p.itinerario !== filterSublinea) return false;
      return true;
    });
  }, [sublineasGeojson, lineByCode, passFilter, filterSublinea]);

  // Cuando se filtra por línea, automáticamente mostrar sublíneas
  const efectivoSublineas = showSublineas || !!filterLinea;
  // Cuando se filtra por línea, automáticamente mostrar paradas
  const efectivoParadas = showParadas || !!filterLinea;

  const maxConsumo = useMemo(
    () => Math.max(1, ...mainLines.map((l) => l.consumo_anual_estimado_kwh)),
    [mainLines]
  );
  const weightFor = (l) => 2 + (l.consumo_anual_estimado_kwh / maxConsumo) * 6;

  // Paradas a mostrar
  const paradasMostrar = useMemo(() => {
    if (!efectivoParadas) return [];
    // Si hay línea filtrada/sublínea filtrada, solo las paradas de esa
    if (filterLinea && paradasLinea) {
      const set = new Map();
      const subs = filterSublinea
        ? sublineasDisponibles.filter((s) => s.itinerario === filterSublinea)
        : sublineasDisponibles;
      subs.forEach((s) => s.paradas.forEach((p) => set.set(p.codigo, p)));
      return Array.from(set.values());
    }
    // Si no hay línea filtrada pero showParadas=true, mostrar todas
    if (paradasGeojson) {
      return paradasGeojson.features.map((f) => ({
        codigo: f.properties.codigo,
        nombre: f.properties.nombre,
        municipio: f.properties.municipio,
        lon: f.geometry.coordinates[0],
        lat: f.geometry.coordinates[1],
      }));
    }
    return [];
  }, [efectivoParadas, filterLinea, paradasLinea, filterSublinea, sublineasDisponibles, paradasGeojson]);

  const selectedSubs = useMemo(() => {
    if (!selectedLine || !sublineasGeojson) return [];
    return sublineasGeojson.features.filter(
      (f) => f.properties.codigo_linea === selectedLine.codigo_linea
    );
  }, [selectedLine, sublineasGeojson]);

  const openLineDetail = async (linea) => {
    setSelectedLine(linea);
    if (!sublineasGeojson) await loadSublineas();
  };

  const resetFilters = () => {
    setFilterLote(""); setFilterLinea(""); setFilterSublinea("");
    setFilterTamano(""); setFilterViab("");
  };

  const submitCochera = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.lat || !form.lon) return;
    try {
      await api.post("/cocheras", {
        nombre: form.nombre,
        lote: form.lote || null,
        capacidad_vehiculos: parseInt(form.capacidad_vehiculos) || 0,
        lat: parseFloat(form.lat),
        lon: parseFloat(form.lon),
        observaciones: form.observaciones,
      });
      setForm({ nombre: "", lote: "", capacidad_vehiculos: 50, lat: "", lon: "", observaciones: "" });
      setShowCocheraForm(false);
      setPickFromMap(false);
      await refreshCocheras();
    } catch (e) {
      alert("Error al guardar la cochera: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteCochera = async (id) => {
    if (!window.confirm("¿Eliminar esta cochera?")) return;
    await api.delete(`/cocheras/${id}`);
    refreshCocheras();
  };

  if (loading) return <div className="text-gray-500">Cargando mapa…</div>;

  return (
    <div className="space-y-5" data-testid="mapa-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Vista territorial</div>
        <h1 className="section-title mt-1">Mapa de líneas analizadas</h1>
        <p className="section-sub">
          Trazados <strong>reales por calles</strong> (OSRM/OpenStreetMap) sobre las paradas del Anexo VI. Color = viabilidad. Grosor = consumo anual.
        </p>
      </div>

      {geojsonMeta?.available && (
        <div className="crtm-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="geojson-info">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#14532D] flex items-center justify-center flex-shrink-0">
              <FileJson size={17} className="text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">
                GeoJSON oficial · {fmtInt(geojsonMeta.n_features)} sublíneas · {fmtInt(geojsonMeta.n_paradas)} paradas
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Por lote: {lotes.map((lot, i) => (
                  <span key={lot}>{i > 0 ? " · " : ""}{lot} ({geojsonMeta.por_lote?.[lot] ?? 0})</span>
                ))}
                {geojsonMeta.routed && (
                  <span className="ml-2 inline-flex items-center gap-1 text-green-700 font-semibold">
                    <RouteIcon size={11} /> Trazados por calles ({geojsonMeta.n_routed_ok}/{geojsonMeta.n_features})
                  </span>
                )}
                · {geojsonMeta.size_kb} KB
              </div>
            </div>
          </div>
          <a href={`${API}/geojson`} download="crtm_lineas.geojson" className="btn-primary flex-shrink-0" data-testid="btn-download-geojson">
            <Download size={14} /> Descargar GeoJSON
          </a>
        </div>
      )}

      {/* Filters: 2 rows */}
      <div className="crtm-card p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="kpi-label">Lote</label>
            <select value={filterLote} onChange={(e) => setFilterLote(e.target.value)} data-testid="map-filter-lote"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white">
              <option value="">Todos</option>
              {lotes.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="kpi-label">Línea</label>
            <select
              value={filterLinea}
              onChange={(e) => { setFilterLinea(e.target.value); setFilterSublinea(""); }}
              data-testid="map-filter-linea"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
              disabled={!lineasFiltradas.length}
            >
              <option value="">Todas</option>
              {lineasFiltradas.map((l) => {
                const label = `${l.lote} · ${l.numero_linea} — ${l.denominacion.length > 36 ? l.denominacion.slice(0, 36) + "…" : l.denominacion}`;
                return <option key={l.codigo_linea} value={l.codigo_linea}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="kpi-label">Sublínea</label>
            <select
              value={filterSublinea}
              onChange={(e) => setFilterSublinea(e.target.value)}
              data-testid="map-filter-sublinea"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white"
              disabled={!sublineasDisponibles.length}
            >
              <option value="">Todas</option>
              {sublineasDisponibles.map((s) => {
                const label = `${s.sentido} · ${s.itinerario} (${s.n_paradas} paradas)`;
                return <option key={s.itinerario} value={s.itinerario}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="kpi-label">Tamaño vehículo</label>
            <select value={filterTamano} onChange={(e) => setFilterTamano(e.target.value)} data-testid="map-filter-tamano"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white">
              <option value="">Todos</option>
              {TAMANOS_OPTS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="kpi-label">Viabilidad</label>
            <select value={filterViab} onChange={(e) => setFilterViab(e.target.value)} data-testid="map-filter-viab"
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-white">
              <option value="">Todas</option>
              <option>Alta</option>
              <option>Condicionada</option>
              <option>A revisar</option>
            </select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => setShowSublineas(!showSublineas)}
            data-testid="btn-toggle-sublineas"
            className={
              "px-3 py-1.5 rounded-md text-xs font-semibold border inline-flex items-center gap-1.5 " +
              (efectivoSublineas
                ? "bg-[#14532D] text-white border-[#14532D]"
                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300")
            }
            disabled={!!filterLinea}
            title={filterLinea ? "Auto-activado al filtrar por línea" : ""}
          >
            <Layers size={13} />
            {efectivoSublineas ? `${fmtInt(subFeatures.length)} sublíneas` : `${fmtInt(mainLines.length)} líneas`}
          </button>
          <button
            onClick={() => setShowParadas(!showParadas)}
            data-testid="btn-toggle-paradas"
            className={
              "px-3 py-1.5 rounded-md text-xs font-semibold border inline-flex items-center gap-1.5 " +
              (efectivoParadas
                ? "bg-[#14532D] text-white border-[#14532D]"
                : "bg-white text-gray-700 border-gray-200 hover:border-gray-300")
            }
          >
            <CircleIcon size={13} />
            {efectivoParadas ? `${fmtInt(paradasMostrar.length)} paradas` : "Mostrar paradas"}
          </button>
          {(filterLote || filterLinea || filterSublinea || filterTamano || filterViab) && (
            <button onClick={resetFilters} className="text-xs text-gray-500 hover:text-gray-900 ml-2" data-testid="btn-reset-map-filters">
              Limpiar filtros
            </button>
          )}
          <div className="ml-auto flex flex-wrap gap-3 text-xs">
            <Legend color="#15803D" label="Alta" />
            <Legend color="#B45309" label="Condicionada" />
            <Legend color="#991B1B" label="A revisar" />
          </div>
        </div>
      </div>

      {/* Map + side panel */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        <div className="crtm-card p-0 overflow-hidden relative" style={{ height: 640 }} data-testid="mapa-leaflet">
          {pickFromMap && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-[#14532D] text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg flex items-center gap-2 pointer-events-none">
              <MapPin size={13} /> Haz clic en el mapa para situar la cochera
            </div>
          )}
          <MapContainer
            center={[40.45, -3.65]}
            zoom={9}
            style={{ width: "100%", height: "100%", cursor: pickFromMap ? "crosshair" : "" }}
            scrollWheelZoom={true}
          >
            <MapClickHandler
              enabled={pickFromMap}
              onPick={(latlng) => {
                setForm({ ...form, lat: latlng.lat.toFixed(6), lon: latlng.lng.toFixed(6) });
                setPickFromMap(false);
                setShowCocheraForm(true);
              }}
            />
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="CARTO Voyager">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> · &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png${CARTO_KEY ? `?key=${CARTO_KEY}` : ""}`}
                  subdomains="abcd"
                  maxZoom={20}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="CARTO Positron (claro)">
                <TileLayer
                  attribution='&copy; OpenStreetMap · &copy; CARTO'
                  url={`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png${CARTO_KEY ? `?key=${CARTO_KEY}` : ""}`}
                  subdomains="abcd"
                  maxZoom={20}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="CARTO Dark Matter (oscuro)">
                <TileLayer
                  attribution='&copy; OpenStreetMap · &copy; CARTO'
                  url={`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png${CARTO_KEY ? `?key=${CARTO_KEY}` : ""}`}
                  subdomains="abcd"
                  maxZoom={20}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="OpenStreetMap">
                <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              </LayersControl.BaseLayer>
            </LayersControl>

            {/* Polilíneas */}
            {!efectivoSublineas &&
              mainLines.map((l) => {
                const isSelected = selectedLine?.codigo_linea === l.codigo_linea;
                return (
                  <Polyline
                    key={l.codigo_linea}
                    positions={l.geometria.map(([lon, lat]) => [lat, lon])}
                    pathOptions={{
                      color: viabilidadColor(l.viabilidad),
                      weight: isSelected ? weightFor(l) + 3 : weightFor(l),
                      opacity: isSelected ? 1 : 0.78,
                    }}
                    eventHandlers={{ click: () => openLineDetail(l) }}
                  >
                    <LeafletTooltip sticky direction="top"><PolylineTooltip linea={l} /></LeafletTooltip>
                  </Polyline>
                );
              })}

            {efectivoSublineas &&
              subFeatures.map((f) => {
                const linea = lineByCode[f.properties.codigo_linea];
                const isSelected = selectedLine?.codigo_linea === linea.codigo_linea;
                const isVuelta = f.properties.sentido === "Vuelta";
                return (
                  <Polyline
                    key={f.properties.itinerario + "-" + f.properties.sentido}
                    positions={f.geometry.coordinates.map(([lon, lat]) => [lat, lon])}
                    pathOptions={{
                      color: viabilidadColor(linea.viabilidad),
                      weight: isSelected ? 5 : 3,
                      opacity: isSelected ? 1 : 0.75,
                      dashArray: isVuelta ? "8 6" : null,
                    }}
                    eventHandlers={{ click: () => openLineDetail(linea) }}
                  >
                    <LeafletTooltip sticky direction="top">
                      <div className="text-xs">
                        <div className="font-semibold">
                          {linea.lote} · Línea {linea.numero_linea} · {f.properties.sentido}
                        </div>
                        <div className="text-gray-600 truncate max-w-[260px]">{linea.denominacion}</div>
                        <div className="mt-1">
                          Itinerario <strong>{f.properties.itinerario}</strong> · {fmtDec(f.properties.distancia_real_km || f.properties.distancia_km || 0, 1)} km · {f.properties.n_paradas} paradas
                          {f.properties.routed === false && <span className="text-amber-600"> · (recta)</span>}
                        </div>
                      </div>
                    </LeafletTooltip>
                  </Polyline>
                );
              })}

            {/* Paradas */}
            {paradasMostrar.map((p) => (
              <CircleMarker
                key={p.codigo}
                center={[p.lat, p.lon]}
                radius={4}
                pathOptions={{
                  color: "#0F1A14",
                  fillColor: "#FFFFFF",
                  fillOpacity: 1,
                  weight: 2,
                }}
              >
                <LeafletTooltip direction="top">
                  <div className="text-xs">
                    <div className="font-semibold">{p.nombre}</div>
                    <div className="text-gray-500">{p.municipio} · {p.codigo}</div>
                  </div>
                </LeafletTooltip>
              </CircleMarker>
            ))}

            {/* Cocheras */}
            {cocheras.map((c) => (
              <Marker key={c.id} position={[c.lat, c.lon]} icon={cocheraIcon(loteColor(c.lote, lotes))}>
                <Popup>
                  <div className="text-xs min-w-[200px]">
                    <div className="font-semibold text-sm">{c.nombre}</div>
                    {c.lote && (
                      <div className="mt-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
                           style={{ background: loteColor(c.lote, lotes) + "20", color: loteColor(c.lote, lotes) }}>
                        {c.lote}
                      </div>
                    )}
                    <div className="mt-2 text-gray-600">
                      <div>Capacidad: <strong>{fmtInt(c.capacidad_vehiculos)}</strong> vehículos</div>
                      <div className="font-mono mt-1 text-[10px] text-gray-400">
                        {c.lat.toFixed(5)}, {c.lon.toFixed(5)}
                      </div>
                    </div>
                    {c.observaciones && <div className="mt-2 italic text-gray-600">{c.observaciones}</div>}
                    <button
                      onClick={() => deleteCochera(c.id)}
                      className="mt-3 inline-flex items-center gap-1 text-red-700 hover:text-red-900 text-xs font-semibold"
                      data-testid={`delete-cochera-${c.id}`}
                    >
                      <Trash2 size={12} /> Eliminar
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Side panel */}
        <div className="space-y-5">
          {/* Cocheras */}
          <div className="crtm-card" data-testid="cocheras-panel">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-[15px] flex items-center gap-2">
                <Warehouse size={16} /> Cocheras ({cocheras.length})
              </h3>
              <button
                onClick={() => { setShowCocheraForm(!showCocheraForm); setPickFromMap(false); }}
                data-testid="btn-toggle-cochera-form"
                className="text-xs font-semibold inline-flex items-center gap-1 text-[#14532D] hover:text-blue-900"
              >
                <Plus size={13} /> {showCocheraForm ? "Cancelar" : "Añadir"}
              </button>
            </div>
            {showCocheraForm && (
              <form onSubmit={submitCochera} className="px-5 pb-4 space-y-2 border-b border-gray-100" data-testid="cochera-form">
                <input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Nombre (p.ej. Cochera Alcobendas)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm" data-testid="cochera-input-nombre" />
                <div className="grid grid-cols-2 gap-2">
                  <select value={form.lote} onChange={(e) => setForm({ ...form, lote: e.target.value })}
                    className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-white" data-testid="cochera-input-lote">
                    <option value="">Sin lote</option>
                    {lotes.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <input type="number" min="0" value={form.capacidad_vehiculos}
                    onChange={(e) => setForm({ ...form, capacidad_vehiculos: e.target.value })}
                    placeholder="Capacidad" className="px-3 py-2 border border-gray-200 rounded-md text-sm" data-testid="cochera-input-capacidad" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.000001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
                    placeholder="Latitud" required
                    className="px-3 py-2 border border-gray-200 rounded-md text-sm font-mono" data-testid="cochera-input-lat" />
                  <input type="number" step="0.000001" value={form.lon} onChange={(e) => setForm({ ...form, lon: e.target.value })}
                    placeholder="Longitud" required
                    className="px-3 py-2 border border-gray-200 rounded-md text-sm font-mono" data-testid="cochera-input-lon" />
                </div>
                <button type="button" onClick={() => setPickFromMap(true)}
                  className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-md text-xs text-gray-600 hover:border-gray-400 hover:bg-gray-50 inline-flex items-center justify-center gap-1.5"
                  data-testid="btn-pick-from-map">
                  <MapPin size={12} /> Click para situar en el mapa
                </button>
                <textarea value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  placeholder="Observaciones (opcional)" rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm" />
                <button type="submit" className="btn-primary w-full justify-center" data-testid="btn-save-cochera">
                  Guardar cochera
                </button>
              </form>
            )}
            <div className="max-h-[200px] overflow-y-auto">
              {cocheras.length === 0 && !showCocheraForm && (
                <div className="px-5 py-6 text-xs text-gray-500 text-center">
                  Aún no hay cocheras registradas.
                </div>
              )}
              {cocheras.map((c) => (
                <div key={c.id}
                  className="px-5 py-2.5 border-b border-gray-100 last:border-0 hover:bg-gray-50 flex items-start justify-between gap-2"
                  data-testid={`cochera-item-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{c.nombre}</div>
                    <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                      {c.lote && (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold"
                              style={{ background: loteColor(c.lote, lotes) + "20", color: loteColor(c.lote, lotes) }}>
                          {c.lote}
                        </span>
                      )}
                      <span>{fmtInt(c.capacidad_vehiculos)} veh</span>
                    </div>
                  </div>
                  <button onClick={() => deleteCochera(c.id)} className="text-gray-400 hover:text-red-700" title="Eliminar">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Line detail or selected-line paradas */}
          <div className="crtm-card" data-testid="line-detail-panel">
            <div className="px-5 pt-5 pb-3 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-[15px]">
                {filterLinea ? "Línea filtrada" : "Detalle de línea"}
              </h3>
              {selectedLine && !filterLinea && (
                <button onClick={() => setSelectedLine(null)}
                  className="text-gray-400 hover:text-gray-700" data-testid="btn-close-line-detail">
                  <X size={15} />
                </button>
              )}
            </div>
            {!selectedLine && !filterLinea && (
              <div className="px-5 pb-5 text-xs text-gray-500">
                Selecciona una línea en el filtro o haz clic en una polilínea del mapa para ver sus sublíneas y paradas.
              </div>
            )}
            {(selectedLine || filterLinea) && (
              <LineDetailContent
                linea={filterLinea ? lineByCode[filterLinea] : selectedLine}
                sublineas={filterLinea ? sublineasDisponibles.map(s => ({
                  properties: { sentido: s.sentido, itinerario: s.itinerario, n_paradas: s.n_paradas }
                })) : selectedSubs}
                paradasLinea={paradasLinea}
                filterSublinea={filterSublinea}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Legend = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="w-3.5 h-1 rounded" style={{ background: color }} />
    <span className="text-gray-700">{label}</span>
  </span>
);

const PolylineTooltip = ({ linea }) => (
  <div className="text-xs">
    <div className="font-semibold">{linea.lote} · Línea {linea.numero_linea}</div>
    <div className="text-gray-600 max-w-[280px]">{linea.denominacion}</div>
    <div className="mt-1">
      <strong>{fmtDec(linea.consumo_kwh_km_base, 2)} kWh/km</strong> · {fmtMWh(linea.consumo_anual_estimado_kwh)}
    </div>
    <div className="text-gray-500 mt-0.5">Viabilidad: {linea.viabilidad}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="px-1 py-2 bg-gray-50 rounded-md">
    <div className="text-[9px] uppercase font-bold text-gray-500 tracking-wider">{label}</div>
    <div className="font-heading font-bold text-[13px] text-gray-900 mt-0.5">{value}</div>
  </div>
);

function LineDetailContent({ linea, sublineas, paradasLinea, filterSublinea }) {
  if (!linea) return null;
  return (
    <div className="px-5 pb-5">
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <span className="pill neutral">{linea.lote}</span>
        <span className="pill neutral">{linea.tipo_servicio}</span>
        <span className="pill" style={{
          background: viabilidadColor(linea.viabilidad) + "20",
          color: viabilidadColor(linea.viabilidad),
          borderColor: viabilidadColor(linea.viabilidad) + "40",
        }}>{linea.viabilidad}</span>
      </div>
      <div className="font-heading font-bold text-lg leading-tight">Línea {linea.numero_linea}</div>
      <div className="text-xs text-gray-600">{linea.denominacion}</div>
      <div className="mt-3 grid grid-cols-3 gap-1 text-center">
        <Stat label="Long. km" value={fmtDec(linea.longitud_km, 1)} />
        <Stat label="kWh/km" value={fmtDec(linea.consumo_kwh_km_base, 2)} />
        <Stat label="Consumo anual" value={fmtMWh(linea.consumo_anual_estimado_kwh)} />
      </div>

      <div className="mt-4">
        <div className="kpi-label mb-2">Sublíneas ({sublineas.length})</div>
        <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
          {sublineas.length === 0 && <div className="text-xs text-gray-400">Cargando…</div>}
          {sublineas.map((f) => {
            const isVuelta = f.properties.sentido === "Vuelta";
            const isActive = filterSublinea === f.properties.itinerario;
            return (
              <div key={f.properties.itinerario + "-" + f.properties.sentido}
                className={"flex items-center justify-between p-2 border rounded-md text-xs " +
                  (isActive ? "border-gray-900 bg-gray-50" : "border-gray-100")}>
                <div>
                  <div className="font-semibold">
                    <span className={isVuelta ? "text-gray-600" : "text-gray-900"}>{f.properties.sentido}</span>
                    <span className="font-mono text-gray-400 ml-2">{f.properties.itinerario}</span>
                  </div>
                  <div className="text-[11px] text-gray-500">{f.properties.n_paradas} paradas</div>
                </div>
                {f.properties.distancia_real_km || f.properties.distancia_km ? (
                  <div className="font-mono font-semibold">
                    {fmtDec(f.properties.distancia_real_km || f.properties.distancia_km || 0, 2)} km
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Paradas listadas */}
      {paradasLinea && (
        <div className="mt-4">
          <div className="kpi-label mb-2">
            {filterSublinea ? `Paradas (${filterSublinea})` : "Paradas (todas las sublíneas)"}
          </div>
          <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1 text-xs">
            {Object.entries(paradasLinea)
              .flatMap(([sentido, its]) =>
                Object.entries(its)
                  .filter(([it]) => !filterSublinea || it === filterSublinea)
                  .flatMap(([it, paradas]) =>
                    paradas.map((p) => ({ ...p, sentido, itinerario: it }))
                  )
              )
              .slice(0, 50)
              .map((p, i) => (
                <div key={i} className="flex items-start gap-2 py-1 border-b border-gray-50 last:border-0">
                  <span className="text-gray-400 font-mono text-[10px] w-5 flex-shrink-0">{p.orden}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.nombre}</div>
                    <div className="text-[10px] text-gray-400">{p.municipio} · {p.codigo}</div>
                  </div>
                  {p.distancia_km != null && (
                    <span className="text-[10px] text-gray-500 font-mono flex-shrink-0">
                      {fmtDec(p.distancia_km, 1)} km
                    </span>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      <Link to={`/linea/${encodeURIComponent(linea.codigo_linea)}`}
        className="btn-secondary w-full justify-center mt-4" data-testid="btn-open-ficha">
        Ver ficha completa <ChevronRight size={14} />
      </Link>
    </div>
  );
}
